import { useRef, useCallback, useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isMainAppPath, resolveBackTarget } from '../lib/backNavigation';

/* ─── SwipeBack: iOS 风格右滑返回手势 ───
   · 从屏幕左沿 30px 内开始触摸
   · 向右滑动超过 80px 触发返回
   · 拖拽中显示半透明箭头 + 页面偏移
   · Tab 首页不响应（无 back history）
   ─────────────────────────────────── */

const EDGE_ZONE = 30;      // px, left edge touch zone
const TRIGGER_DIST = 80;   // px, min swipe distance to trigger back
const MAX_TRANSLATE = 120; // px, max visual shift

export function SwipeBack({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const containerRef = useRef<HTMLDivElement>(null);

  const touchStartX = useRef(0);
  const touchStartY = useRef(0);
  const isTracking = useRef(false);
  const isHorizontal = useRef<boolean | null>(null);
  const skipTransition = useRef(false);
  const navTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);

  // 使用 ref 追踪 translateX 值，避免 handleTouchEnd 依赖 state 导致 listener 频繁重建
  const translateXRef = useRef(0);
  const setTranslateXTracked = (v: number) => {
    translateXRef.current = v;
    setTranslateX(v);
  };

  const isTabPage = isMainAppPath(location.pathname);

  const handleTouchStart = useCallback((e: TouchEvent) => {
    if (isTabPage) return;
    const touch = e.touches[0];
    if (touch.clientX <= EDGE_ZONE) {
      touchStartX.current = touch.clientX;
      touchStartY.current = touch.clientY;
      isTracking.current = true;
      isHorizontal.current = null;
    }
  }, [isTabPage]);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    if (!isTracking.current) return;
    const touch = e.touches[0];
    const dx = touch.clientX - touchStartX.current;
    const dy = touch.clientY - touchStartY.current;

    // Determine direction on first significant movement
    if (isHorizontal.current === null) {
      if (Math.abs(dx) > 8 || Math.abs(dy) > 8) {
        isHorizontal.current = Math.abs(dx) > Math.abs(dy);
        if (!isHorizontal.current) {
          isTracking.current = false;
          return;
        }
      } else {
        return;
      }
    }

    if (dx > 0) {
      e.preventDefault();
      const clamped = Math.min(dx, MAX_TRANSLATE);
      setTranslateXTracked(clamped);
      setIsDragging(true);
    }
  }, []);

  const handleTouchEnd = useCallback(() => {
    if (!isTracking.current) return;
    isTracking.current = false;

    if (translateXRef.current >= TRIGGER_DIST) {
      setTranslateXTracked(window.innerWidth);
      navTimerRef.current = setTimeout(() => {
        skipTransition.current = true;
        setTranslateXTracked(0);
        setIsDragging(false);
        navigate(resolveBackTarget(location.pathname));
      }, 180);
    } else {
      setTranslateXTracked(0);
      setIsDragging(false);
    }
  }, [navigate]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener('touchstart', handleTouchStart, { passive: true });
    el.addEventListener('touchmove', handleTouchMove, { passive: false });
    el.addEventListener('touchend', handleTouchEnd, { passive: true });
    el.addEventListener('touchcancel', handleTouchEnd, { passive: true });
    return () => {
      el.removeEventListener('touchstart', handleTouchStart);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleTouchEnd);
      el.removeEventListener('touchcancel', handleTouchEnd);
    };
  }, [handleTouchStart, handleTouchMove, handleTouchEnd]);

  // Cleanup navigation timer on unmount
  useEffect(() => {
    return () => {
      if (navTimerRef.current) clearTimeout(navTimerRef.current);
    };
  }, []);

  // Reset on route change; clear skipTransition after one frame
  useEffect(() => {
    setTranslateX(0);
    setIsDragging(false);
    if (skipTransition.current) {
      // Keep transition disabled for this render, re-enable next frame
      requestAnimationFrame(() => { skipTransition.current = false; });
    }
  }, [location.pathname]);

  const progress = Math.min(translateX / TRIGGER_DIST, 1);
  const showIndicator = isDragging && !isTabPage;
  const noTransition = isDragging || skipTransition.current;

  return (
    <div ref={containerRef} style={{ position: 'relative', minHeight: '100vh', overflow: 'hidden' }}>
      {/* Back indicator — left edge shadow + chevron */}
      {showIndicator && (
        <div style={{
          position: 'fixed', top: 0, left: 0, bottom: 0, width: Math.min(translateX + 20, 60),
          background: `linear-gradient(to right, rgba(28,25,23,${0.08 * progress}), transparent)`,
          zIndex: 9999,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          transition: noTransition ? 'none' : 'all 0.2s ease',
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={`rgba(44,42,41,${progress * 0.6})`}
            strokeWidth="2" strokeLinecap="round"
            style={{
              transform: `translateX(${Math.min(translateX * 0.3, 16) - 20}px) scale(${0.6 + progress * 0.4})`,
              transition: noTransition ? 'none' : 'all 0.2s ease',
            }}>
            <polyline points="15 18 9 12 15 6" />
          </svg>
        </div>
      )}

      {/* Content */}
      <div style={{
        transform: translateX > 0 ? `translateX(${translateX}px)` : 'none',
        transition: noTransition ? 'none' : 'transform 0.25s cubic-bezier(0.2, 0, 0, 1)',
        willChange: isDragging ? 'transform' : 'auto',
      }}>
        {children}
      </div>
    </div>
  );
}
